import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { ROOT } from "@/routes";
import { CONFIG } from "@/config";
import { CAPTION, LABEL, SECTION_TITLE } from "@/lib/typography";
import * as Sentry from "@sentry/react";
import { isChunkLoadError, reloadOnceForChunkError } from "@/utils/lazy-with-retry";
import { cn } from "@/lib/utils";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chunk loading errors (stale deployment, network blip) — reload to get fresh assets.
    // reloadOnceForChunkError has a 10s cooldown to prevent infinite loops.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return;
    }

    // Log error to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Send error to Sentry with context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: true,
      },
      extra: {
        errorInfo: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Log to our debug logger if available
    if (typeof window !== "undefined" && (window as any).debugLog) {
      (window as any).debugLog.write("REACT_ERROR_BOUNDARY", {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        componentStack: errorInfo.componentStack,
      });
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Save error info to state for display
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = ROOT();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle className={SECTION_TITLE}>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We're sorry, but something unexpected happened. You can try refreshing the page or going back to the
                homepage.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show error details in development */}
              {CONFIG.IS_DEVELOPMENT && this.state.error && (
                <div className="p-3 bg-muted rounded-md space-y-2">
                  <p className={cn(LABEL, "text-muted-foreground")}>Error Details (Development Only):</p>
                  <p className={cn(CAPTION, "font-mono break-all")}>{this.state.error.toString()}</p>
                  {this.state.error.stack && (
                    <details className="text-ui-caption">
                      <summary className="cursor-pointer text-muted-foreground">
                        Stack trace
                      </summary>
                      <pre className="mt-2 overflow-x-auto text-muted-foreground">{this.state.error.stack}</pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={() => window.location.reload()} variant="outline" className="flex-1">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Page
                </Button>
                <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>
              </div>

              {/* Try again button for soft reset */}
              <Button variant="ghost" onClick={this.handleReset} className="w-full">
                Try Again Without Refreshing
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Optional: Hook for functional components to trigger error boundary
export function useErrorHandler() {
  return (error: Error) => {
    throw error;
  };
}
